/**
 * EGDC V4 Cart — drawer toggle + AJAX qty/remove + section re-render.
 *
 * <v4-cart-drawer>
 *   - Listens for clicks on [data-cart-toggle] anywhere in the document
 *     (the bag icon in the header) → opens.
 *   - Closes via [data-cart-close], scrim, or Esc.
 *   - Locks body scroll while open.
 *   - Delegates clicks on [data-qty-step] (± buttons) and [data-cart-remove]
 *     to call /cart/change.js, then re-fetches this section via Shopify's
 *     Section Rendering API and replaces its own children with the response.
 *   - Updates header bag count after every cart change.
 *
 * Auto-opens after a successful POST to /cart/add.js fired by the rest of
 * the theme (PDP form submit) — listens on the global `cart:added` event.
 */

class V4CartDrawer extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.lastTrigger = null;

    // Document-scoped delegation so the toggle works even though it lives
    // inside the header (different DOM subtree).
    this.onDocumentClick = this.onDocumentClick.bind(this);
    document.addEventListener('click', this.onDocumentClick);

    document.addEventListener('keydown', this.onKey);

    // Auto-open + refresh when something is added via AJAX from the PDP.
    document.addEventListener('v4:cart-added', this.onCartAdded);

    // Internal delegation for qty + remove + close (X button).
    this.addEventListener('click', this.onInternalClick);

    // Capture-phase outside-click handler. Capture fires before any bubble-
    // phase stopPropagation, and document is above any subtree, so this
    // catches the click regardless of what third-party scripts do.
    document.addEventListener('click', this.onOutsideClick, true);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('v4:cart-added', this.onCartAdded);
    this.removeEventListener('click', this.onInternalClick);
    document.removeEventListener('click', this.onOutsideClick, true);
  }

  onOutsideClick = (event) => {
    if (!this.classList.contains('is-open')) return;
    // Don't close on clicks inside the panel itself, or on the bag-icon
    // toggle that opened the drawer (would re-open immediately).
    if (event.target.closest('.v4-cart-drawer__panel')) return;
    if (event.target.closest('[data-cart-toggle]')) return;
    this.close();
  };

  onCartAdded = async () => {
    // /cart/add.js returns the line item but not cart totals, so fetch
    // /cart.js for item_count and refresh the section markup.
    try {
      const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (cartRes.ok) {
        const cart = await cartRes.json();
        this.updateBubble(cart.item_count);
      }
      await this.refresh();
    } catch (error) {
      console.warn('V4 cart-added refresh failed:', error);
    }
    // Open after refresh so the new item is visible the instant the panel slides in.
    this.open();
  };

  onDocumentClick(event) {
    const toggle = event.target.closest('[data-cart-toggle]');
    if (toggle) {
      event.preventDefault();
      this.lastTrigger = toggle;
      this.open();
      return;
    }
  }

  onInternalClick = (event) => {
    if (event.target.closest('[data-cart-close]')) {
      event.preventDefault();
      this.close();
      return;
    }
    const stepBtn = event.target.closest('[data-qty-step]');
    if (stepBtn) {
      event.preventDefault();
      const line = Number(stepBtn.dataset.line);
      const step = Number(stepBtn.dataset.qtyStep);
      const valueEl = stepBtn.parentElement.querySelector('[data-qty-value]');
      const current = Number(valueEl ? valueEl.textContent.trim() : 0);
      const next = Math.max(0, current + step);
      this.changeLine(line, next);
      return;
    }
    const removeBtn = event.target.closest('[data-cart-remove]');
    if (removeBtn) {
      event.preventDefault();
      const line = Number(removeBtn.dataset.line);
      this.changeLine(line, 0);
      return;
    }
  };

  onKey = (event) => {
    if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
  };

  open = () => {
    this.classList.add('is-open');
    document.body.classList.add('v4-no-scroll');
    this.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => {
      const focusable = this.querySelector('[data-cart-close]');
      if (focusable) focusable.focus();
    });
  };

  close = () => {
    this.classList.remove('is-open');
    document.body.classList.remove('v4-no-scroll');
    this.setAttribute('aria-hidden', 'true');
    if (this.lastTrigger) this.lastTrigger.focus();
  };

  async changeLine(line, quantity) {
    this.classList.add('is-loading');
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      if (!res.ok) throw new Error(`Cart change failed: ${res.status}`);
      const cart = await res.json();
      this.updateBubble(cart.item_count);
      await this.refresh();
    } catch (error) {
      console.warn('V4 cart change failed:', error);
    } finally {
      this.classList.remove('is-loading');
    }
  }

  async refresh() {
    const url = `${window.location.pathname}?section_id=${this.sectionId}`;
    const res = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error(`Section refresh failed: ${res.status}`);
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector('v4-cart-drawer');
    if (!next) throw new Error('v4-cart-drawer missing in section response');
    this.replaceChildren(...next.childNodes);
  }

  updateBubble(count) {
    const bubble = document.querySelector('.topnav__cart-bubble');
    if (!bubble) return;
    if (count > 0) {
      bubble.textContent = count;
      bubble.style.display = '';
    } else {
      bubble.style.display = 'none';
    }
  }
}

if (!customElements.get('v4-cart-drawer')) {
  customElements.define('v4-cart-drawer', V4CartDrawer);
}
